import React, { useEffect, useState } from 'react';
import { useOffline, useOfflineQueue } from '../hooks/useOffline';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { WifiOff, Wifi, Upload, CheckCircle2 } from 'lucide-react';
import { cn } from './ui/utils';
import { toast } from 'sonner';

export const OfflineIndicator: React.FC = () => {
  const isOffline = useOffline();
  const { queue, processQueue } = useOfflineQueue();
  const [showDetails, setShowDetails] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (!isOffline && queue.length > 0) {
      // Auto-sync when coming back online
      handleSync();
    }
  }, [isOffline]);

  const handleSync = async () => {
    if (queue.length === 0) return;

    setIsSyncing(true);
    try {
      await processQueue(async (item) => {
        // TODO: Process each queued action
        console.log('Processing queued action:', item);
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 500));
      });
      toast.success('Offline changes synced!');
    } catch {
      toast.error('Failed to sync some changes');
    } finally {
      setIsSyncing(false);
    }
  };

  if (!isOffline && queue.length === 0) {
    return null;
  }

  return (
    <>
      {/* Floating Indicator */}
      <div className="fixed bottom-4 right-4 z-50">
        <Badge
          variant={
            isOffline
              ? 'destructive'
              : queue.length > 0
                ? 'default'
                : 'secondary'
          }
          className={cn(
            'cursor-pointer gap-2 px-3 py-2 shadow-lg transition-all hover:scale-105',
            'animate-in fade-in slide-in-from-bottom-2'
          )}
          onClick={() => setShowDetails(!showDetails)}
        >
          {isOffline ? (
            <>
              <WifiOff className="h-4 w-4 animate-pulse" />
              <span className="font-medium">Offline Mode</span>
            </>
          ) : queue.length > 0 ? (
            <>
              <Upload className="h-4 w-4" />
              <span className="font-medium">{queue.length} pending</span>
            </>
          ) : (
            <>
              <Wifi className="h-4 w-4" />
              <span className="font-medium">Online</span>
            </>
          )}
        </Badge>
      </div>

      {/* Details Card */}
      {showDetails && (
        <Card
          className={cn(
            'fixed bottom-20 right-4 z-50 w-80 shadow-xl',
            'animate-in fade-in slide-in-from-bottom-2'
          )}
        >
          <div className="p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold">
                {isOffline ? 'Offline Mode' : 'Sync Status'}
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-muted-foreground hover:text-foreground cursor-pointer"
              >
                ✕
              </button>
            </div>

            {isOffline ? (
              <div className="space-y-3">
                <div className="rounded-md bg-yellow-50 p-3 dark:bg-yellow-900/20">
                  <div className="flex gap-2">
                    <WifiOff className="h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-500" />
                    <div className="text-sm">
                      <p className="font-medium text-yellow-800 dark:text-yellow-200">
                        No internet connection
                      </p>
                      <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
                        Your changes will be saved and synced when you&apos;re
                        back online.
                      </p>
                    </div>
                  </div>
                </div>

                {queue.length > 0 && (
                  <div>
                    <p className="mb-2 text-sm font-medium">
                      Pending Actions ({queue.length})
                    </p>
                    <div className="space-y-1">
                      {queue.slice(0, 3).map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center gap-2 rounded-md bg-muted px-2 py-1.5 text-xs"
                        >
                          <div className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
                          <span className="capitalize">
                            {item.action.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                      {queue.length > 3 && (
                        <p className="px-2 text-xs text-muted-foreground">
                          +{queue.length - 3} more
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : queue.length > 0 ? (
              <div className="space-y-3">
                <div className="rounded-md bg-blue-50 p-3 dark:bg-blue-900/20">
                  <div className="flex gap-2">
                    <Upload className="h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-800 dark:text-blue-200">
                        Ready to sync
                      </p>
                      <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                        {queue.length} action{queue.length > 1 ? 's' : ''}{' '}
                        waiting to be synced
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  className="w-full"
                  onClick={handleSync}
                  disabled={isSyncing}
                >
                  {isSyncing ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Sync Now
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="rounded-md bg-green-50 p-3 dark:bg-green-900/20">
                <div className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-400" />
                  <div className="text-sm">
                    <p className="font-medium text-green-800 dark:text-green-200">
                      All synced
                    </p>
                    <p className="mt-1 text-xs text-green-700 dark:text-green-300">
                      All your changes are up to date
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </>
  );
};
